import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FadeLoader } from 'react-spinners';
import {
  Card, Box, Grid, Divider, Container, Typography, Button, Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Page from '../../components/Page';
import tracker from '../../api/tracker';
import { generateRegistrationPdf } from '../../utils/registrationPdf';

const override = { margin: '0 auto' };

export default function RegistrationView() {
  const { hashid } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const response = await tracker.get(`registrations/${hashid}`);
      setRegistration(response.data.data);
      setLoading(false);
    })();
  }, [hashid]);

  const reg = registration;

  const getStatusColor = (status) => {
    const colorMap = {
      rejected: { color: '#FF003F', bg: '#F8E3E1' },
      pending:  { color: '#6d8ee9', bg: '#e0e9fa' },
    };
    const scheme = colorMap[status] || { color: '#6ca181', bg: '#dff8e7' };
    return {
      padding: '1px 10px',
      borderRadius: '30px',
      fontWeight: 700,
      fontSize: '13px',
      display: 'inline-block',
      color: scheme.color,
      backgroundColor: scheme.bg,
    };
  };

  const addressStr = reg
    ? [reg.address_line_1, reg.address_line_2, reg.city, reg.state, reg.zip].filter(Boolean).join(', ')
    : '';

  return (
    <Page title={t('registration')}>
      <Container maxWidth="xl">
        {loading || !reg ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <FadeLoader cssOverride={override} color="#032C65" />
          </Box>
        ) : (
          <Card sx={{ p: 3 }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5" fontWeight={700}>{reg.family_name}</Typography>
                <span style={getStatusColor(reg.status)}>{t(reg.status)}</span>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/dashboard/registration')}
                >
                  {t('Go_back')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={isPrinting ? undefined : <PrintIcon />}
                  disabled={isPrinting}
                  onClick={async () => {
                    setIsPrinting(true);
                    try { await generateRegistrationPdf(reg); } finally { setIsPrinting(false); }
                  }}
                  sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
                >
                  {isPrinting ? t('loading') : t('print')}
                </Button>
              </Stack>
            </Stack>

            {/* Section cards */}
            <Grid container spacing={3}>
              {/* Family Information */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {t('section_family_info') || 'Información Familiar'}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <InfoRow label={t('family_name')} value={reg.family_name} />
                  <InfoRow label={t('husband_name')} value={reg.husband_name} />
                  <InfoRow label={t('wife_name')} value={reg.wife_name} />
                  <InfoRow label={t('Address')} value={addressStr} />
                  <InfoRow label={t('Phone')} value={reg.phone} />
                  <InfoRow label={t('Language')} value={reg.language} />
                </Card>
              </Grid>

              {/* Children */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {t('section_children') || 'Hijos'}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {reg.children && Array.isArray(reg.children) && reg.children.length > 0 ? (
                    reg.children.map((child, i) => (
                      <Box key={i} sx={{ mb: 1.5, pb: 1.5, borderBottom: i < reg.children.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <InfoRow label={t('name')} value={child.name} />
                        {child.age && <InfoRow label={t('Age')} value={child.age} />}
                        {child.gender && <InfoRow label={t('Gender')} value={child.gender} />}
                        {child.birth_date && <InfoRow label={t('birth_date')} value={child.birth_date} />}
                        {child.school_grade && <InfoRow label={t('school_grade') || 'Grado escolar'} value={child.school_grade} />}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">{t('no_children') || 'Sin hijos registrados'}</Typography>
                  )}
                </Card>
              </Grid>

              {/* Family Members */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {t('section_family_members') || 'Miembros de la Familia'}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {reg.family_members && Array.isArray(reg.family_members) && reg.family_members.length > 0 ? (
                    reg.family_members.map((member, i) => (
                      <Box key={i} sx={{ mb: 1.5, pb: 1.5, borderBottom: i < reg.family_members.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <InfoRow label={t('name')} value={member.name} />
                        {member.relation && <InfoRow label={t('Relation')} value={member.relation} />}
                        {member.age && <InfoRow label={t('Age')} value={member.age} />}
                        {member.phone && <InfoRow label={t('Phone')} value={member.phone} />}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">{t('no_family_members') || 'Sin miembros registrados'}</Typography>
                  )}
                </Card>
              </Grid>

              {/* Interests */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {t('section_interests') || 'Intereses'}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {reg.interests && Array.isArray(reg.interests) && reg.interests.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {reg.interests.map((interest, i) => (
                        <Box
                          key={i}
                          sx={{
                            bgcolor: '#eef2ff',
                            color: '#032C65',
                            borderRadius: 1.5,
                            px: 1.5,
                            py: 0.5,
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          {t(interest) || interest}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">{t('no_interests') || 'Sin intereses registrados'}</Typography>
                  )}
                </Card>
              </Grid>
            </Grid>
          </Card>
        )}
      </Container>
    </Page>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'right' }}>{value || '—'}</Typography>
    </Box>
  );
}